import React, { useState, useEffect } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import { DateTime } from 'luxon';
import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { currency } from 'app/src/utils/stringHelpers';
import styled from 'styled-components/native';
import { Button } from 'app/src/elements/buttons';

import {
  RECONCILE_TXN_LINES, UPDATE_TXN_LINE, UPDATE_RECONCILE
} from './queries';

const CalculationContainer = styled(View)`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  gap: 50px;
`;

const BalanceContainer = styled(View)`
  flex-direction: column;
  flex: 0.95;
`;

const BalanceContainerUpper = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`;

const BalanceContainerLower = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  padding: 3% 5% 3% 5%;
`;

const DifferenceContainer = styled(View)`
  justify-content: center;
  justify-items: center;
  border-left-color: black;
  border-left-width: 1px;
  padding: 0 5% 0 5%;
`;

const BeginBalance = styled(View)`

`;

const EndBalance = styled(View)`

`;

const ClearedBalance = styled(View)`

`;

const Payments = styled(View)`

`;

const Deposits = styled(View)`

`;

const Difference = styled(View)`

`;

const CalculationOperator = styled(Text)`
  font-size: 24px;
`;

const moneyLarge = {
  fontSize: '30px'
}

const moneySmall = {
  fontSize: '24px'
}


const Perform = ({ reconcile, setShowModal }) => {
  const [lines, setLines] = useState([]);
  const [clearedBalance, setClearedBalance] = useState(0.0);
  const [difference, setDifference] = useState(reconcile.endBalance - clearedBalance);
  const [payments, setPayments] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const { data, refetch } = useQuery(RECONCILE_TXN_LINES,
    { variables: { id: reconcile.id }}
  );

  const [updateTxnLine] = useMutation(UPDATE_TXN_LINE, {
    onCompleted: () => {
      refetch();
    }
  });

  const [updateReconcile] = useMutation(UPDATE_RECONCILE, {
    onCompleted: () => {
      setShowModal(false);
      refetch();
    }
  });

  useEffect(() => {
    if(data) {
      let newPayments = [];
      let newDeposits = [];
      let newClearedBalance = 0.0;

      setLines([...data.reconcileTxnLines]);

      data.reconcileTxnLines.map(line => {
        if(line.reconcileId) {
          if(parseFloat(line.debitLessCredit) <= 0) {
            newPayments.push(line);
            newClearedBalance -= parseFloat(line.debitLessCredit);
          }else {
            newDeposits.push(line);
            newClearedBalance += parseFloat(line.debitLessCredit);
          }


        }
      });

      setPayments(newPayments);
      setDeposits(newDeposits);
      setClearedBalance(newClearedBalance);
      setDifference(parseFloat(reconcile.endBalance) - newClearedBalance);
    }
  }, [data])

  const columns = [
    {
      key: 'reconcileId',
      label: '',
      content: ({ entry }) => {
        return (
          <input
            type='checkbox'
            checked={entry.reconcileId}
            onChange={(e) => {
              updateTxnLine({
                variables: {
                  id: entry.id,
                  reconcileId: e.target.checked ? reconcile.id : ''
                }
              })
            }}
          />
        )
      },
    },
    {
      key: 'bankDate',
      label: 'Bank Date',
      sort: 'desc',
      content: ({ entry }) => (
        DateTime.fromISO(entry.txn.bankDate).toLocaleString(DateTime.DATE_SHORT)
      ),
    },
    {
      key: 'type',
      label: 'Type',
      content: ({ entry }) => (
        entry.txn.type
      ),
    },
    {
      key: 'referenceNumber',
      label: 'Reference Number',
      content: ({ entry }) => entry.txn.reference,
    },
    {
      key: 'name',
      label: 'Name',
      content: ({ entry }) => entry.txn.name
    },
    {
      key: 'payment',
      label: 'Payment',
      content: ({ entry }) => {
        if(entry.debitLessCredit < 0) {
          return currency(Math.abs(entry.debitLessCredit));
        }
      },
    },
    {
      key: 'deposit',
      label: 'Deposit',
      content: ({ entry }) => {
        if(entry.debitLessCredit > 0) {
          return currency(entry.debitLessCredit);
        }
      },
    },
  ];

  if (!data) { return <DataState.Loading /> };

  const getPaymentsTotal = () => {
    let total = payments.reduce((n, {debitLessCredit}) => n + parseFloat(debitLessCredit), 0);
    return Math.abs(total);
  }

  const getDepositsTotal = () => {
    return deposits.reduce((n, {debitLessCredit}) => n + parseFloat(debitLessCredit), 0);
  }

  const handleSubmit = () => {
    let variables = {...reconcile};
    variables['completedAt'] = DateTime.utc().toISO();
    variables['accountId'] = reconcile.account.id;
    updateReconcile({variables: variables})
  }

  return (
    <MainContainer>
      <ContentContainer>
        <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
          <PageTitle>{`Reconcile ${reconcile.account.fullName}`}</PageTitle>
          {difference === 0 ?
            <Button label="Complete Reconcile" onPress={() => handleSubmit()} /> :
            null
          }
        </View>
        <CalculationContainer>
          <BalanceContainer>
            <BalanceContainerUpper>
              <EndBalance>
                <Text style={moneyLarge}>
                  {currency(reconcile.endBalance)}
                </Text>
                <Text>STATEMENT ENDING BALANCE</Text>
              </EndBalance>
              <CalculationOperator>
                -
              </CalculationOperator>
              <ClearedBalance>
                <Text style={moneyLarge}>
                  {currency(clearedBalance)}
                </Text>
                <Text>CLEARED BALANCE</Text>
              </ClearedBalance>
            </BalanceContainerUpper>
            <BalanceContainerLower>
              <BeginBalance>
                <Text style={moneySmall}>
                  {currency(reconcile.startBalance)}
                </Text>
                <Text>BEGINNING BALANCE</Text>
              </BeginBalance>
              <CalculationOperator>-</CalculationOperator>
              <Payments>
                <Text style={moneySmall}>
                  {currency(getPaymentsTotal())}
                </Text>
                <Text>{`${payments.length} PAYMENTS`}</Text>
              </Payments>
              <CalculationOperator>+</CalculationOperator>
              <Deposits>
                <Text style={moneySmall}>
                  {currency(getDepositsTotal())}
                </Text>
                <Text>{`${deposits.length} DEPOSITS`}</Text>
              </Deposits>
            </BalanceContainerLower>
          </BalanceContainer>
          <DifferenceContainer>
            <Difference>
              <Text style={moneyLarge}>
                {currency(Math.abs(difference))}
              </Text>
              <Text>
                DIFFERENCE
              </Text>
            </Difference>
          </DifferenceContainer>
        </CalculationContainer>

        <DataTable.Simple
          data={lines}
          columns={columns}
          length='all'
        />
      </ContentContainer>
    </MainContainer>
  )
}

export default Perform;